'use client';

import React, { useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import Button from '@/components/atoms/project-builder/button';
import Spinner from '@/components/organisms/spinner';
import Popup from '@/components/atoms/project-builder/popup';
import Icon from '@/components/icons/x-mark';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { errorData } from './data';
import useProjectBuilder from '@/lib/hooks/useProjectBuilder';

interface ProductUploadProps {
  customerId?: string;
  projectId?: string;
  projectForm?: any;
}

interface ExcelRow {
  sku: string;
  qty?: number;
  section?: string;
  remarks?: string;
}

// const validFileTypes = [
//   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
//   'text/csv',
// ];
const validExtensions = ['csv'];


const markDuplicates = (rows: ExcelRow[]): ExcelRow[] => {
  const seen = new Set<string>();

  return rows.map((item) => {
    const key = `${item.sku}_${item.section ?? ''}`;
    const remarks = item.remarks ? item.remarks.split(';').map(r => r.trim()) : [];

    if (seen.has(key)) {
      remarks.push('Duplicate SKU and section combinations were found. Quantities have been combined.');
    } else {
      seen.add(key);
    }

    return {
      ...item,
      remarks: remarks.join('; ')
    };
  });
};


const validateRow = (
  item: ExcelRow,
  addedSkus: string[],
  notAddedSkus: string[]
): ExcelRow => {
  const remarks: string[] = item.remarks ? item.remarks.split(';').map(r => r.trim()) : [];

  if (!item.sku) remarks.push(errorData.skuNotAvailable);

  if (item.sku && /[^a-zA-Z0-9\s.\-]/.test(item.sku)) {
    remarks.push(errorData.skuInvalid);
  }


  if (!item.qty) remarks.push(errorData.qtyNotAvailable);
  if (item.qty) {
    if (typeof item.qty !== 'number' || isNaN(item.qty) || item.qty <= 0) {
      remarks.push(errorData.qtyInvalid);
    } else if (item.qty > 9999) {
      remarks.push(errorData.qtyExceedsMax);
    }
  }

  if (!item.section) remarks.push(errorData.sectionNotAvailable);

  const trimmedSection = item.section?.trim();
  if (trimmedSection && (trimmedSection.length > 30 || /[^a-zA-Z0-9\s]/.test(trimmedSection))) {
    remarks.push(errorData.sectionInvalid);
  }
  if (!remarks.length) {
    const normalizedSku = item.sku ? String(item.sku).toUpperCase() : '';
    if (addedSkus.includes(normalizedSku)) remarks.push(errorData.addedSuccessfully);
    if (notAddedSkus.includes(normalizedSku)) remarks.push(errorData.productNotFound);
  }
  const { qty, section, sku } = item;
  return {
    sku,
    qty,
    section,
    remarks: [...new Set(remarks)].join('; '),
  };
};

const getSortedData = (
  data: ExcelRow[],
  addedSkus: string[],
  notAddedSkus: string[]
): ExcelRow[] => {
  const sortRank = (remarks: string = '') => {
    if (remarks.includes(errorData.qtyNotAvailable)) return 1;
    if (remarks.includes(errorData.qtyInvalid)) return 2;
    if (remarks.includes(errorData.qtyExceedsMax)) return 2.5;
    if (remarks.includes(errorData.sectionNotAvailable)) return 3;
    if (remarks.includes(errorData.sectionInvalid)) return 4;
    if (remarks.includes(errorData.skuNotAvailable)) return 5;
    if (remarks.includes(errorData.skuInvalid)) return 6;
    if (remarks.includes(errorData.addedSuccessfully)) return 7;
    if (remarks.includes(errorData.productNotFound)) return 8;
    return 9;
  };

  const validatedRows = data.map(row => validateRow(row, addedSkus, notAddedSkus));
  const withDuplicatesMarked = markDuplicates(validatedRows);

  return validatedRows.sort((a, b) => sortRank(a.remarks) - sortRank(b.remarks));
};

const hasUnexpectedColumns = (data: any[]): boolean => {
  const allowedKeys = ['sku', 'qty', 'section'];
  return data.some(row =>
    Object.keys(row).some(
      key => !allowedKeys.includes(key.toLowerCase())
    )
  );
};

const ProductUpload = ({ customerId, projectId, projectForm }: ProductUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { locale } = useParams();
  const { getProjectById } = useProjectBuilder();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenError, setIsOpenError] = useState(false);
  const [addedSkus, setAddedSkus] = useState<string[]>([]);
  const [notAddedSkus, setNotAddedSkus] = useState<string[]>([]);
  const [jsonData, setJsonData] = useState<ExcelRow[]>([]);
  const [fileError, setFileError] = useState('');
  const [uploadResponse, setUploadResponse] = useState<{ code: string; message: string; } | null>(null);

  const triggerFileUpload = () => fileInputRef.current?.click();
  const resetFileInput = () => fileInputRef.current && (fileInputRef.current.value = '');

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !validExtensions.includes(fileExtension)) {
      setFileError('Oops, Quick fix! Use the template provided in My Projects to get started');
      setIsOpenError(true);
      resetFileInput();
      return;
    }


    setLoading(true);
    setFileError('');
    setIsOpenError(false);
    setIsOpen(false);
    setAddedSkus([]);
    setNotAddedSkus([]);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data: ExcelRow[] = XLSX.utils.sheet_to_json(sheet);

      if (!data.length) {
        throw new Error(errorData.emptyFile);
      } else if (data.length > 250) {
        throw new Error(errorData.moreItems);
      }

      if (hasUnexpectedColumns(data)) {
        setFileError(errorData.inValidFileFormat);
        throw new Error(errorData.inValidFileFormat);
      }

      let latestProjectForm = projectForm;
      if (projectId) {
        try {
          const projectData: any = await getProjectById({ shoppingListId: projectId });
          if (projectData?.data) {
            latestProjectForm = projectData.data;
          }
        } catch (error) {
          console.warn('Failed to fetch latest project data, using provided projectForm:', error);
        }
      }

      const existingSkuRoomQuantities: Record<string, number> = {};

      const lineItemsToCheck = latestProjectForm?.lineItems || latestProjectForm?.roomGroups?.flatMap((room: any) =>
        room.items?.map((item: any) => ({
          ...item,
          roomName: room.roomName || room.name
        })) || []
      ) || [];

      if (lineItemsToCheck.length > 0) {
        lineItemsToCheck.forEach((item: any, index: number) => {
          if (!item) return;

          const rawSku = item?.variant?.attributes?.['item-code'] || item.sku || item.productSku || '';
          const rawRoom = item.roomName || item.section || '';

          if (!rawSku || !rawRoom) {
            return;
          }

          let roomName = String(rawRoom).trim();
          let sku = String(rawSku).toUpperCase().trim();

          const quantity = Number(item.quantity) || 0;
          if (quantity <= 0) {
            console.log(`[Validation] Item ${index} skipped - invalid quantity: ${quantity}`);
            return;
          }

          const key = `${sku}_${roomName}`;

          console.log(`[Validation] Item ${index}: Raw SKU: "${rawSku}" -> Normalized: "${sku}", Room: "${rawRoom}" -> Normalized: "${roomName}", Qty: ${quantity}, Key: "${key}"`);

          existingSkuRoomQuantities[key] =
            (existingSkuRoomQuantities[key] || 0) + quantity;
        });
      }

      const csvSkuRoomTotals: Record<string, number> = {};

      data.forEach((row, index) => {
        const rawSection = row.section?.toString().trim() || '';
        const rawSku = row.sku?.toString().trim() || '';

        if (!rawSection || !rawSku) {
          console.log(`[Validation] CSV Row ${index} skipped - missing section or SKU. Section: "${rawSection}", SKU: "${rawSku}"`);
          return;
        }

        let section = rawSection.trim();
        let sku = rawSku.toUpperCase().trim();

        const qty =
          typeof row.qty === 'number' &&
            !isNaN(row.qty) &&
            row.qty > 0
            ? row.qty
            : 0;

        const key = `${sku}_${section}`;

        csvSkuRoomTotals[key] =
          (csvSkuRoomTotals[key] || 0) + qty;
      });

      for (const [key, newQty] of Object.entries(csvSkuRoomTotals)) {
        const existingQty = existingSkuRoomQuantities[key] || 0;
        const firstUnderscoreIndex = key.indexOf('_');
        const sku = key.substring(0, firstUnderscoreIndex);
        const roomName = key.substring(firstUnderscoreIndex + 1);
        if (existingQty === 0) {
          const matchingKeys = Object.keys(existingSkuRoomQuantities).filter(k => k.includes(sku) || k.includes(roomName));
          if (matchingKeys.length > 0) {
            console.log(`[Validation]   - ⚠️ No exact match, but found similar keys:`, matchingKeys);
          }
        }

        if (existingQty >= 9999) {
          setFileError(
            `No worries — just a quick fix. Quantities need to be 9,999 or less.`
          );
          setIsOpenError(true);
          setLoading(false);
          resetFileInput();
          return;
        }

        const totalAfterAdd = existingQty + newQty;
        if (totalAfterAdd > 9999) {
          setFileError(
            `No worries — just a quick fix. Quantities need to be 9,999 or less.`
          );
          setIsOpenError(true);
          setLoading(false);
          resetFileInput();
          return;
        }
      }


      setJsonData(data);

      const updatedData = data.map(row => {
        const section = row.section?.toString().trim();
        if (section && !/\d+$/.test(section)) {
          return { ...row, section: `${section} 1` };
        }
        return row;
      });

      // Rebuild Excel with updated data
      const updatedSheet = XLSX.utils.json_to_sheet(updatedData);
      const updatedWorkbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(updatedWorkbook, updatedSheet, 'Sheet1');
      const updatedBuffer = XLSX.write(updatedWorkbook, { type: 'array', bookType: 'csv' });

      const formData = new FormData();
      formData.append('file', new Blob([arrayBuffer], { type: file.type }), file.name);
      formData.append('projectId', projectId ?? '');
      formData.append('customerId', customerId ?? '');

      const response = await fetch(`/${locale}/api/upload-excel/`, { method: 'POST', body: formData });
      const result = await response.json();
      if (response.status === 401) {
        throw new Error(result?.message || 'Unauthorized.');
      }

      if (!response.ok) {
        setFileError(result?.message);
        setIsOpen(true);
        setUploadResponse(result);
        return;
      }

      setUploadResponse(result);
      setAddedSkus(result.addedSkus || []);
      setNotAddedSkus(result.notAddedSkus || []);
      setIsOpen(true);

      if (!result?.addedSkus?.length && result?.notAddedSkus?.length) {
        setFileError(errorData.inValidProducts);
        throw new Error(errorData.inValidProducts);
      }

    } catch (error: any) {
      setFileError(error.message || 'Upload failed.');
      setIsOpenError(true);
    } finally {
      setLoading(false);
      resetFileInput();
    }
  };

  const handleDownload = () => {
    const sorted = getSortedData(jsonData, addedSkus, notAddedSkus);
    const worksheet = XLSX.utils.json_to_sheet(sorted);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'Error file with validation remarks.csv');
  };

  const hasValidationIssues = jsonData.some(
    item => !item.sku || /[^a-zA-Z0-9\s.\-]/.test(item.sku) || !item.qty || typeof item.qty !== 'number' || isNaN(item.qty) ||
      item.qty <= 0 || item.qty > 9999 || !item.section || item.section?.length > 30 || /[^a-zA-Z0-9\s]/.test(item.section)
  );

  const handleClose = () => {
    setIsOpen(false);
    setIsOpenError(false);
    resetFileInput();
  };

  return (
    <>
      {loading && <><Spinner />
        <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
      </>}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Button
        variant="secondary"
        className="w-[169px] cursor-pointer h-[45px] border !py-[12px] !px-[24px] !border-[#A6C9CC] !bg-[#A6C9CC] text-sm font-medium flex items-center justify-center gap-[2px]"
        type="button"
        onClick={triggerFileUpload}
        data-html2canvas-ignore
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          height={20}
          width={20}
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-[20px] h-[20px]"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
          />
        </svg>
        Upload CSV
      </Button>

      <Popup isOpen={isOpenError} bodyClassNames="sm:mx-unset max-w-[375px]">
        <div className="p-[25px]">
          <div className="flex justify-between items-start">
            <p className="mt-[10px] font-medium">
              Upload Error
            </p>
            <div className="cursor-pointer ml-4 mt-5" onClick={handleClose}>
              <Icon />
            </div>
          </div>
          <div className="mt-[20px] text-sm text-red-500">
            {fileError}
          </div>
        </div>
      </Popup>

      <Popup isOpen={isOpen} bodyClassNames="sm:mx-unset max-w-[375px]">
        <div className="p-[25px]">
          <div className="flex justify-between items-start">
            <p className="mt-[10px]">
              Upload file operation -{' '}
              {hasValidationIssues ? (
                fileError ? (
                  <span className="text-red-600">{uploadResponse?.code}</span>
                ) : (
                  <span className="text-red-600">Partial Success</span>
                )
              ) : (
                <span className={uploadResponse?.code === 'SUCCESS' ? 'text-green-600' : 'text-red-600'}>
                 {uploadResponse?.code}
                </span>
              )}
            </p>
            <div className="cursor-pointer ml-[16px] mt-[20px]" onClick={handleClose}>
              <Icon />
            </div>
          </div>

          <p className={`mt-[20px] mb-[20px] ${fileError ? "text-red-600" : ""}`}>{uploadResponse?.message}</p>

          {(notAddedSkus.length > 0 || hasValidationIssues) && (
            <div className="mt-[28px]">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDownload();
                }}
                className="!text-blue-600 !underline !hover:text-blue-800"
              >
                Error file with validation remarks
              </a>
            </div>
          )}
        </div>
      </Popup>
    </>
  );
};

export default ProductUpload;
